/**
 * Single-row legend rail with paging controls.
 *
 * A wrapping legend is honest but expensive: every extra line pushes the plot
 * down, and because the wrap point depends on the label text, two charts side
 * by side end up with plots at different heights. This keeps the legend to one
 * row whatever the label count, and pages the overflow instead.
 *
 * The controls appear ONLY when the row actually overflows, so a legend that
 * fits is indistinguishable from a plain row -- no empty chrome, no reserved
 * gutter. Overflow is measured from the live layout rather than guessed from
 * the item count, since label widths vary and the container is responsive.
 *
 * Paging moves by one viewport width, which is why the page count is
 * `scrollWidth / clientWidth` rather than a per-item calculation: an item that
 * straddles the boundary stays reachable from both pages instead of being
 * clipped out of each.
 */

import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "../ui/utils";

export interface LegendPagerProps {
  /**
   * The legend row itself. Must lay out on a single line and size to its
   * content -- `flex w-max flex-nowrap` -- or there is no overflow to page.
   */
  children: ReactNode;
  className?: string;
  /** Accessible name for the scrolling region. */
  ariaLabel?: string;
}

export function LegendPager({
  children,
  className,
  ariaLabel = "Chart legend",
}: LegendPagerProps) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(0);

  const measure = useCallback(() => {
    const el = trackRef.current;
    if (!el || el.clientWidth <= 0) return;
    // +1px of slack: sub-pixel layout rounding otherwise reports a row that
    // fits exactly as overflowing, and shows controls that page nowhere.
    const total = Math.ceil((el.scrollWidth - 1) / el.clientWidth);
    const next = Math.max(1, total);
    setPages(next);
    setPage((p) => Math.min(p, next - 1));
  }, []);

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    measure();
    // Guarded the same way `useElementWidth` is: jsdom has no ResizeObserver
    // and there is no polyfill in the jest setup, so a bare `new` here would
    // take down every test that renders a chart.
    if (typeof ResizeObserver === "undefined") return;
    // The row re-flows on container resize AND on font load, both of which
    // change scrollWidth without changing the children.
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [measure, children]);

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    // jsdom stubs the element but not `scrollTo`; fall back to the property.
    if (typeof el.scrollTo === "function") {
      el.scrollTo({ left: page * el.clientWidth, behavior: "smooth" });
    } else {
      el.scrollLeft = page * el.clientWidth;
    }
  }, [page]);

  const overflowing = pages > 1;
  const atStart = page === 0;
  const atEnd = page >= pages - 1;

  return (
    <div className={cn("flex min-w-0 items-center gap-2", className)}>
      <div
        ref={trackRef}
        role="group"
        aria-label={ariaLabel}
        // `overflow-x-hidden` not `auto`: the pager IS the scrollbar, and a
        // native one under a legend reads as a second, competing control.
        className="min-w-0 flex-1 overflow-x-hidden"
      >
        {children}
      </div>

      {overflowing && (
        <div className="flex shrink-0 items-center gap-0.5 text-muted-foreground">
          <button
            type="button"
            aria-label="Previous legend page"
            disabled={atStart}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            className="grid size-6 place-items-center rounded-md transition-colors hover:bg-muted hover:text-foreground disabled:pointer-events-none disabled:opacity-35 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <ChevronLeft className="size-4" aria-hidden />
          </button>
          <span className="px-0.5 text-[11px] tabular-nums" aria-live="polite">
            {page + 1}/{pages}
          </span>
          <button
            type="button"
            aria-label="Next legend page"
            disabled={atEnd}
            onClick={() => setPage((p) => Math.min(pages - 1, p + 1))}
            className="grid size-6 place-items-center rounded-md transition-colors hover:bg-muted hover:text-foreground disabled:pointer-events-none disabled:opacity-35 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <ChevronRight className="size-4" aria-hidden />
          </button>
        </div>
      )}
    </div>
  );
}
